/*
 * http://jasonwatmore.com/post/2018/10/29/angular-7-user-registration-and-login-example-tutorial
 *
 * The Error Interceptor intercepts http responses from the api to check if there were any errors.
 * If there is a 401 Unauthorized response the token is either refreshed or when session timeout occurs,
 * user is logged out and sent to login page. Other errors are re-thrown up to the calling service so
 * an alert can be displayed to the user.
*/

import { Injectable } from '@angular/core';
import { HttpRequest, HttpHandler, HttpEvent, HttpInterceptor, HttpErrorResponse } from '@angular/common/http';
import { Observable, BehaviorSubject, throwError } from 'rxjs';
import { catchError, switchMap, filter, take, finalize, delay, tap } from 'rxjs/operators';
import { Router } from '@angular/router';
import { AuthenticationService } from '@services/authentication.service';
import { PersistenceService } from '@services/persistence.service';
import { LoadingService } from '@app/_services/loading.service';


@Injectable()
export class RequestInterceptor implements HttpInterceptor {

  private isRefreshing = false;
  private tokenSubject: BehaviorSubject<any> = new BehaviorSubject<any>(null);

  constructor(
    private authenticationService: AuthenticationService,
    private router: Router,
    private persistence: PersistenceService,
    private loadingService: LoadingService
  ) { }

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {

    // loading service is responsible for starting spinners (kupari-button, paged-state) from http requests and it's removed with finalize operator
    this.loadingService.loaderStart();
    let url = request?.url ?? "";
    url = url.toLowerCase();
    if(url.includes("report/report")){
      request = request.clone({withCredentials : true});
    }

    return next.handle(request).pipe(
      catchError(error => {
        // Token won't be refreshed with some requests.
        if (
          request.url.includes("refreshtoken") ||
          request.url.includes("login")
        ) {
          /* If refreshing token fails (refresh token has expired), send user to login page.
           * Also when switching tenant, user is thrown to login page.
           */
          if (request.url.includes("refreshtoken") || request.url.includes("orglogin")) {
            this.logout(true);
          }
          return throwError(error);
        }

        // Save any report errors so they can be displayed in the report popup.
        if (request.url.includes('report')) {
          this.persistence.set('avux-report-error', error);
        }

        // Error handling depends on HTTP status returned. Currently only status 401 has its own handler.
        if (error instanceof HttpErrorResponse) {
          switch ((<HttpErrorResponse>error).status) {
            case 401:
              return this.handle401Error(request, next);
            default:
              return throwError(error);
          }
        } else {
          return throwError(error);
        }
      }),
      finalize(() => {
        // added delay in order to get rid of overlapping requests (loader starting and stopping unnecessarily)
        setTimeout(() => { this.loadingService.loaderStop() }, 300);
      })
    );
  }

  private handle401Error(request: HttpRequest<any>, next: HttpHandler) {
    if(request.url.toLowerCase().includes("externalassignment")) {
      return next.handle(request)
    }
    if (!this.isRefreshing) {
      this.isRefreshing = true;

      // subject is set to null so that subsequent API calls will wait until the new token has been retrieved
      this.tokenSubject.next(null);

      return this.authenticationService.refreshToken().pipe(
        switchMap((tokenData: any) => {
          if (tokenData) {
            this.tokenSubject.next(tokenData);
            return next.handle(this.addToken(request));
          }
          this.logout(true);
          return throwError('Token could not be retrieved');
        }),
        catchError(error => {
          // Should never occur. An exception occurred when calling refreshToken().
          if (error.status === 401) {
            this.logout(false);
            return throwError(error);
          }

          return throwError(error);
        }),
        finalize(() => {
          this.isRefreshing = false;
        }));
    } else {
      return this.tokenSubject.pipe(
        filter(tokenData => tokenData !== null),
        take(1),
        switchMap(() => next.handle(this.addToken(request))));
    }
  }

  private addToken(request: HttpRequest<any>) {
    const token = this.authenticationService.getJwtToken();
    if (!token) {
      return request;
    }
    return request.clone({
      setHeaders: {
        'Authorization': `Bearer ${token}`
      }
    });
  }

  /**
   * Logs out and redirects user back to login page
   * @param timeout When true, sessiontimeout query parameter is passed to login page.
   */
  private logout(timeout: boolean) {
    this.authenticationService.logout();
    if (timeout) {
      this.router.navigate(['login'], { queryParams: { sessiontimeout: 1 } });
    }
    else {
      this.router.navigate(['login']);
    }
  }
}
